import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const getUserRedirectPath = (userRole) => {
  // Redirect based on user role
  if (userRole === 'SUPERADMIN' || userRole === 'ADMIN') {
    return '/admin';
  }
  // For MANAGER and MANUFACTURER roles, go to manufacturer page
  return '/manufacturer';
};

const TremendousCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { id } = useParams(); // Get id from /organizations/:id route (if using that route)
  const { apiCall, user } = useAuth();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Get member_id from URL path (/organizations/:id) or query params (/callback?member_id=...)
  const memberIdFromPath = id ? String(id) : null;
  const memberIdFromQuery = searchParams.get('member_id');
  const memberId = memberIdFromPath || memberIdFromQuery;

  useEffect(() => {
    const handleCallback = async () => {
      // Wait for user to be loaded if not available yet
      if (!user) {
        // User context might still be loading, wait a bit
        return;
      }

      // Get code from URL - check both search params and hash
      const code = searchParams.get('code') || new URLSearchParams(window.location.hash.substring(1)).get('code');
      
      // Get company_id
      // member_id can come from URL path (/organizations/:id) or query params (/callback?member_id=...)
      // We need to look up company_id from member_id
      let companyId = null;
      let isResolvedCompanyId = false; // Track if we successfully resolved company_id from member_id
      
      if (memberId) {
        // Look up company_id from member_id
        try {
          const lookupResult = await apiCall('get', `/api/tremendous/company-from-member/${memberId}`);
          if (lookupResult.success && lookupResult.data?.company_id) {
            companyId = lookupResult.data.company_id;
            isResolvedCompanyId = true;
          }
        } catch (lookupError) {
          console.error('Failed to lookup company_id from member_id:', lookupError);
          // Fall through to use user's company_id or query params
        }
      }
      
      // Fallback to query params or user context
      if (!companyId) {
        const fromQuery = searchParams.get('company_id') || searchParams.get('id');
        // Only use query param if it's not the same as the member_id
        if (fromQuery && fromQuery !== String(memberId)) {
          companyId = parseInt(fromQuery);
        } else {
          companyId = user?.company_id || null;
        }
      }
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setError(`OAuth error: ${errorParam}`);
        setLoading(false);
        const redirectPath = user?.role ? getUserRedirectPath(user.role) : '/manufacturer';
        setTimeout(() => {
          navigate(redirectPath);
        }, 3000);
        return;
      }

      if (!code) {
        // If no code, check if this might be a manual redirect (user clicked "Back to SMOD")
        // In this case, check if Tremendous connection already exists
        console.log('No code parameter found. Checking if Tremendous is already connected...');
        
        // Only check balance if we have a valid company_id (not member_id)
        // Only check if we successfully resolved company_id from member_id or got it from other sources
        if (companyId && (isResolvedCompanyId || companyId !== memberId)) {
          try {
            const checkResult = await apiCall('get', `/api/tremendous/balance/${companyId}`);
            if (checkResult.success) {
              // Already connected, redirect to success page
              const redirectPath = user?.role ? getUserRedirectPath(user.role) : '/manufacturer';
              navigate(`${redirectPath}?tremendous_success=true&company_id=${companyId}&message=already_connected`);
              return;
            }
          } catch (checkError) {
            console.log('Balance check failed, Tremendous not connected:', checkError);
          }
        }
        
        // If not connected and no code, show error
        setError('Missing required parameter: code. Please complete the OAuth authorization on Tremendous and let it redirect you automatically. Do not click "Back to SMOD" manually.');
        setLoading(false);
        const redirectPath = user?.role ? getUserRedirectPath(user.role) : '/manufacturer';
        setTimeout(() => {
          navigate(redirectPath);
        }, 5000);
        return;
      }

      if (!companyId) {
        setError('Missing required parameter: company_id. Please ensure you are logged in.');
        setLoading(false);
        const redirectPath = user?.role ? getUserRedirectPath(user.role) : '/manufacturer';
        setTimeout(() => {
          navigate(redirectPath);
        }, 3000);
        return;
      }

      try {
        // Call backend API to exchange code for tokens
        // company_id is optional - backend will use authenticated user's company_id if not provided
        // member_id from URL (path or query) is the exact one Tremendous used, so pass it to ensure redirect_uri matches
        const result = await apiCall('post', '/api/tremendous/oauth/exchange', {
          code: code,
          ...(companyId && { company_id: parseInt(companyId) }),
          ...(memberId && { member_id: String(memberId) })  // Pass member_id from URL (path or query)
        });

        if (result.success) {
          // Redirect based on user role
          const redirectPath = user?.role ? getUserRedirectPath(user.role) : '/manufacturer';
          navigate(`${redirectPath}?tremendous_success=true&company_id=${companyId}`);
        } else if (!result.requiresAuth) {
          setError(result.error || 'Failed to complete OAuth flow');
          setLoading(false);
          const redirectPath = user?.role ? getUserRedirectPath(user.role) : '/manufacturer';
          setTimeout(() => {
            navigate(redirectPath);
          }, 3000);
        }
      } catch (err) {
        console.error('Error exchanging OAuth code:', err);
        setError('Failed to complete OAuth flow. Please try again.');
        setLoading(false);
        const redirectPath = user?.role ? getUserRedirectPath(user.role) : '/manufacturer';
        setTimeout(() => {
          navigate(redirectPath);
        }, 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, apiCall, user]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2
        }}
      >
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          Completing Tremendous connection...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
          p: 3
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          {error}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Redirecting...
        </Typography>
      </Box>
    );
  }

  return null;
};

export default TremendousCallback;
